import { useEffect, useMemo } from "react";
import moment from "moment";
import { staffTrackingStatusOptions } from "constants";
import { useGetStaffTracking } from "repositories/office-policy-management-repository";

/** Stable empty rows reference so consumers (e.g. selection context) do not re-run effects every render. */
const EMPTY_STAFF_TABLE_ROWS = [];
const PENDING_STATUS_ID =
  staffTrackingStatusOptions.find((status) => status.name === "Pending")?.id ?? 2;
/**
 * Custom hook to fetch credentials data based on active tab and filters
 * @param {string} activeTab - Active tab ("1" for staff, "2" for office)
 * @param {string} selectedId - Owner ID
 * @param {Object} filters - Filter object from useStaffTrackingFilter
 * @returns {Object} Data, loading states, and refetch functions
 */
export const useStaffTrackingData = (activeTab,selectedId, filters,canAccessThisPage,setStaffTrackingPayload) => {
  const {
    startDateApplied,
    endDateApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,
    statusesApplied,
    sortBy,
    isDescending,
    searchValueApplied,
    radioCheckedApplied,
  } = filters;

  // Prepare API payload for staff credentials
  const staffPayload = useMemo(
    () => {
      
      const statuses = [...statusesApplied];
      if (radioCheckedApplied && !statuses.includes(PENDING_STATUS_ID)) {
        statuses.push(PENDING_STATUS_ID);
      }
      return ({
      ownerId: selectedId,
      assignedOnStartDate: startDateApplied
        ? moment(startDateApplied).format("YYYY-MM-DD")
        : null,
        assignedOnEndDate: endDateApplied
        ? moment(endDateApplied).format("YYYY-MM-DD")
        : null,
      userIds: selectedEmpFilterApplied,
      statuses,
      sortBy: sortBy,
      isDescending: isDescending,
      officeIds: selectedOfficeFilterApplied,
      searchTerm: searchValueApplied,
      IsArchived:activeTab==="2"
    })}, [selectedId, activeTab, startDateApplied, endDateApplied, selectedEmpFilterApplied, statusesApplied, radioCheckedApplied, sortBy, isDescending, searchValueApplied,selectedOfficeFilterApplied],
  );

  const staffCredentialsQuery = useGetStaffTracking(staffPayload, {
    enabled: !!selectedId && canAccessThisPage,
  });

  const rowsFromApi = staffCredentialsQuery?.data?.data;
  const tableData = useMemo(() => {
    if (!Array.isArray(rowsFromApi)) return EMPTY_STAFF_TABLE_ROWS;
    if (rowsFromApi.length === 0) return EMPTY_STAFF_TABLE_ROWS;
    return rowsFromApi;
  }, [rowsFromApi]);

  useEffect(() => {
    if (setStaffTrackingPayload && staffPayload && canAccessThisPage) {
      setStaffTrackingPayload(staffPayload);
    }
  }, [staffPayload, setStaffTrackingPayload, canAccessThisPage]);

  return {
    staffCredentials: {
      ...staffCredentialsQuery,
      ...(staffCredentialsQuery?.data || {}),
      data: tableData,
      isLoading:
        staffCredentialsQuery.isLoading || staffCredentialsQuery.isFetching,
      refetch: staffCredentialsQuery.refetch,
    },
  };
};
